import React, { useState } from "react";
import {
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
} from "@fluentui/react-tag-picker";
import { Skeleton, SkeletonItem, Tag, makeStyles } from "@fluentui/react-components";
import {
  tagPickerListPositioning,
  useTagPickerOptionLayoutStyles,
} from "./TagPickerOptionLayout";

const useStyles = makeStyles({
  list: {
    maxHeight: "300px",
    overflowY: "auto",
  },
});

export interface SourceMultiTagPickerOption {
  id: string;
  label: string;
  secondaryLabel?: string;
}

interface SourceMultiTagPickerProps {
  selectedIds: string[];
  query: string;
  placeholder: string;
  options: SourceMultiTagPickerOption[];
  onQueryChange: (value: string) => void;
  onSelectionChange: (selectedIds: string[]) => void;
  minQueryLength?: number;
  typeToSearchText?: string;
  disabled?: boolean;
  isLoading?: boolean;
}

export const SourceMultiTagPicker: React.FC<SourceMultiTagPickerProps> = ({
  selectedIds,
  query,
  placeholder,
  options,
  onQueryChange,
  onSelectionChange,
  minQueryLength = 2,
  typeToSearchText,
  disabled,
  isLoading,
}) => {
  const styles = useStyles();
  const optionLayout = useTagPickerOptionLayoutStyles();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const trimmedQuery = query.trim();
  const canSearch = trimmedQuery.length >= minQueryLength;
  const selectedIdSet = new Set(selectedIds);
  const minQueryInfoValue = "__minQueryLength";

  const selectedOptions = options.filter((option) =>
    selectedIdSet.has(option.id),
  );
  const availableOptions = options.filter(
    (option) => !selectedIdSet.has(option.id),
  );

  // The skeleton is for the first load only. A search reloads the options on every
  // keystroke, and swapping the picker out then unmounted the input: focus went, the
  // list closed, and the results only showed once the user reopened it.
  if (isLoading && !isInputFocused && trimmedQuery === "") {
    return (
      <Skeleton>
        <SkeletonItem shape="rectangle" size={32} />
      </Skeleton>
    );
  }

  return (
    <TagPicker
      positioning={tagPickerListPositioning}
      selectedOptions={selectedIds}
      disabled={disabled}
      open={isOpen}
      onOpenChange={(event, data) => {
        // Fluent closes the list on every option click. Several picks in a row meant reopening
        // it each time, and a quick second click could land after it closed and be lost.
        // Escape, Tab and clicking away still close it.
        const target = event.target as Element | null;
        if (!data.open && data.type === "click" && target?.closest?.('[role="option"]')) {
          return;
        }
        setIsOpen(data.open);
      }}
      onOptionSelect={(_, data) => {
        // The "type to search" row is a hint, not an option: selecting it
        // would otherwise add its sentinel value to the selection.
        if (data.value === minQueryInfoValue) {
          return;
        }

        // Picking an option ends the search, so the term goes with it.
        // Leaving it behind stranded a half-typed word next to the tag it had
        // already found, and left the list filtered to that word while the
        // user went looking for the next one.
        onQueryChange("");

        onSelectionChange((data.selectedOptions ?? []).map(String));
      }}
    >
      <TagPickerControl>
        <TagPickerGroup>
          {selectedOptions.map((option) => (
            <Tag key={option.id} value={option.id}>
              {option.label}
            </Tag>
          ))}
        </TagPickerGroup>
        <TagPickerInput
          value={query}
          placeholder={selectedIds.length > 0 ? "" : placeholder}
          onChange={(event) => {
            onQueryChange(event.currentTarget.value);
          }}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
        />
      </TagPickerControl>
      <TagPickerList className={styles.list}>
        {!canSearch && typeToSearchText ? (
          <TagPickerOption text={typeToSearchText} value={minQueryInfoValue}>
            {typeToSearchText}
          </TagPickerOption>
        ) : (
          availableOptions.map((option) => (
            <TagPickerOption
              key={option.id}
              value={option.id}
              text={option.label}
              className={optionLayout.option}
              secondaryContent={
                option.secondaryLabel
                  ? { children: option.secondaryLabel, className: optionLayout.secondaryContent }
                  : undefined
              }
            >
              {option.label}
            </TagPickerOption>
          ))
        )}
      </TagPickerList>
    </TagPicker>
  );
};
